<template lang="pug">
.login-qr-code
  title-main.login-qr-code__title.align-center
    slot(name='loginTitle')
  .login-qr-code__content
    .login-qr-code__content__invalid(v-if='errorMessages.length > 0')
      .login-qr-code__content__invalid__message(v-for='message in errorMessages') {{ message }}
    //- QRコードリーダー部
    .login-qr-code__content__reader
      //- カメラモニター画像表示部
      .login-qr-code__content__reader__camera(v-if='showCameraView')
        .login-qr-code__content__reader__camera__title
          slot(name='cameraTitle')
        .login-qr-code__content__reader__camera__monitor
          //- カメラモニター画像表示キャンバス
          canvas.login-qr-code__content__reader__camera__monitor__canvas(ref='cameraMonitor')
          //- カメラローディング表示
          .login-qr-code__content__reader__camera__monitor__loading(v-if='loading')
            .login-qr-code__content__reader__camera__monitor__loading__text.font-bold
              slot(name='cameraLoadingText')
          //- カメラモード切り替えボタン
          button-base.login-qr-code__content__reader__camera__monitor__switch(v-else, @click='switchCameraMode')
            image-base.login-qr-code__content__reader__camera__monitor__switch__icon(
              :src='require("@/assets/img/qr/camera-switch.svg")'
            )
      //- カメラ画面表示開始ボタン表示部
      .login-qr-code__content__reader__start(v-else)
        button-next.login-qr-code__content__reader__start__button(@click='startShowCamera')
          image-base.login-qr-code__content__reader__start__button__icon(:src='require("@/assets/img/qr/qr.png")')
          .login-qr-code__content__reader__start__button__text
            slot(name='startCameraText')
  link-base.login-qr-code__link(@click='moveLinkEvent')
    slot(name='moveLinkText')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import TitleMain from '@/components/atoms/TitleMain.vue'
import LinkBase from '@/components/atoms/LinkBase.vue'
import ButtonBase from '@/components/atoms/ButtonBase.vue'
import ImageBase from '@/components/atoms/ImageBase.vue'
import ButtonNext from '@/components/molecules/student/ButtonNext.vue'
import jsQR, { QRCode } from 'jsqr'

const QR_CODE_LINE_DRAW_COLOR = '#FF3B58'
const QR_CODE_LINE_DRAW_WIDTH = 4

/**
 * ログイン画面（QRコード）
 */
@Component({
  components: {
    TitleMain,
    LinkBase,
    ButtonBase,
    ImageBase,
    ButtonNext,
  },
})
export default class LoginQrCode extends Vue {
  /** カメラ画面表示フラグ */
  private showCameraView = false
  /** 映像デバイス読み込み中フラグ */
  private loading = false
  /** カメラ映像表示用canvasアニメーションID */
  private animationId?: number

  /** デバイス映像ストリーム用 */
  private videoStreamElem: HTMLVideoElement = document.createElement('video')
  /** カメラ対面モード（初期値）設定 */
  /**
   * TODO: デフォルトをINカメラにする場合は下記の記述とする
   * private cameraFacingMode: 'user' | 'environment' = 'user'
   */
  private cameraFacingMode: 'user' | 'environment' = 'environment'

  // 移動リンク押下時イベント
  @Prop()
  moveLinkEvent!: Function

  // エラーメッセージ
  @Prop()
  errorMessages!: string[]

  // カメラ画面表示開始ボタン使用フラグ
  @Prop({ default: true })
  useStartCameraButton!: boolean

  /** カメラ映像描画先キャンバス */
  @Ref() cameraMonitor!: HTMLCanvasElement

  private async mounted() {
    // 表示開始ボタンを使用しない場合は自動的に表示する
    if (!this.useStartCameraButton) {
      await this.startShowCamera()
    }
  }

  // カメラ画面を表示する
  private async startShowCamera(): Promise<void> {
    this.showCameraView = true
    await this.startMediaDevice()
  }

  // カメラモード切り替え処理
  private async switchCameraMode(): Promise<void> {
    this.cameraFacingMode = this.cameraFacingMode === 'user' ? 'environment' : 'user'
    await this.stopMediaDevice()
    await this.startMediaDevice()
  }

  /**
   * 映像デバイス情報取得・更新、カメラ映像解析処理開始
   */
  public async startMediaDevice(): Promise<void> {
    const video: HTMLVideoElement = this.videoStreamElem
    const canvasAnimationRender: () => void = this.canvasAnimationRender

    this.loading = true
    await navigator.mediaDevices.getUserMedia({ video: { facingMode: this.cameraFacingMode } }).then(function (
      stream: MediaStream
    ): void {
      video.srcObject = stream
      video.setAttribute('playsinline', 'true') // required to tell iOS safari we don't want fullscreen
      video.play()
      // カメラ映像表示用canvasアニメーション開始
      requestAnimationFrame(canvasAnimationRender)
    })
  }

  /**
   * 映像ストリーム準備中判定
   */
  private isVideoOnLoad(): boolean {
    return !!this.videoStreamElem && this.videoStreamElem.readyState === this.videoStreamElem.HAVE_ENOUGH_DATA
  }

  /**
   * カメラ映像表示用canvasアニメーション処理
   */
  private canvasAnimationRender(): void {
    this.animationId = requestAnimationFrame(this.canvasAnimationRender)
    // 要素が失われたらアニメーション停止（ページ遷移対策）
    if (!this.cameraMonitor) {
      this.stopMediaDevice()
      return
    }

    // 映像ストリームが有効になるまで処理しない
    if (this.isVideoOnLoad()) {
      // 映像ストリームサイズを、カメラ映像表示キャンバスサイズに適用
      if (this.loading) {
        this.cameraMonitor.width = this.videoStreamElem.videoWidth
        this.cameraMonitor.height = this.videoStreamElem.videoHeight
        this.loading = false
        return
      }

      // カメラ映像表示用キャンバスに映像ストリーム描画
      const canvas = this.cameraMonitor.getContext('2d', {
        willReadFrequently: true,
      }) as CanvasRenderingContext2D
      canvas.drawImage(this.videoStreamElem, 0, 0, this.cameraMonitor.width, this.cameraMonitor.height)

      // カメラ映像表示用キャンバスから映像情報取得
      const imageData: ImageData = canvas.getImageData(0, 0, this.cameraMonitor.width, this.cameraMonitor.height)
      // QRコード解析
      const qrCode: QRCode | null = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      })

      // QRコード検出判定
      if (qrCode && qrCode.data !== '') {
        // 検出されたQRコード部分にライン描画
        this.drawQrCodeDetectionLine(qrCode.location)
        this.stopMediaDevice()
        this.$emit('read-qr-code', qrCode.data)
      }
    }
  }

  /**
   * 映像デバイス情報取得・更新、カメラ映像解析終了
   */
  private async stopMediaDevice(): Promise<void> {
    // カメラ映像表示用canvasアニメーション停止
    if (this.animationId) cancelAnimationFrame(this.animationId)

    const mediaStream = this.videoStreamElem.srcObject as MediaStream
    if (!mediaStream) return

    // デバイス映像ストリーム終了
    const tracks = mediaStream.getTracks()
    tracks.forEach((track: MediaStreamTrack) => {
      track.stop()
      mediaStream.removeTrack(track)
    })
    this.videoStreamElem.srcObject = null
  }

  /**
   * QRコード検出ライン描画
   */
  private drawQrCodeDetectionLine(qrLocat: QRCode['location']) {
    this.drawLine(qrLocat.topLeftCorner, qrLocat.topRightCorner)
    this.drawLine(qrLocat.topRightCorner, qrLocat.bottomRightCorner)
    this.drawLine(qrLocat.bottomRightCorner, qrLocat.bottomLeftCorner)
    this.drawLine(qrLocat.bottomLeftCorner, qrLocat.topLeftCorner)
  }

  /**
   * カメラ映像表示用キャンバスにライン描画
   */
  private drawLine(begin: { x: number; y: number }, end: { x: number; y: number }) {
    const canvas = this.cameraMonitor.getContext('2d') as CanvasRenderingContext2D

    canvas.beginPath()
    canvas.moveTo(begin.x, begin.y)
    canvas.lineTo(end.x, end.y)
    canvas.lineWidth = QR_CODE_LINE_DRAW_WIDTH
    canvas.strokeStyle = QR_CODE_LINE_DRAW_COLOR
    canvas.stroke()
  }
}
</script>

<style lang="stylus" scoped>
.login-qr-code
  margin 100px auto 0
  max-width 520px

  &__title
    margin-bottom 20px

  &__content
    margin-bottom 10px

    &__invalid
      &__message
        color var(--color-login-alert__text)

    &__reader
      margin-bottom 20px

      &__camera
        display flex
        flex-direction column
        align-items center

        &__monitor
          position relative
          display flex
          align-items center
          justify-content center
          margin-top 15px
          width 100%

          &__loading
            position absolute
            display flex
            align-items center
            justify-content center
            z-index 1
            width 100%
            height 100%
            background-color var(--color-login-qr-code-loading__background)

            &__text
              color var(--color-login-qr-code-loading__text)

          &__canvas
            width 100%

          &__switch
            position absolute
            top 0
            left 0
            z-index 2

      &__start
        &__button
          display flex
          justify-content center

          &__icon
            margin-right 5px

  &__link
    display flex
    justify-content center
    color var(--color-default__link-text)
</style>
